import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { AlertTriangle, X } from 'lucide-react'
import { useDialogFocus } from '../hooks/useDialogFocus'

/**
 * ConfirmDialog, in-app replacement for window.confirm.
 *
 * Usage:
 *   const confirm = useConfirm()
 *   const ok = await confirm({
 *     title: 'Aus der Konferenz entfernen?',
 *     message: 'Max Mustermann wird sofort getrennt.',
 *     confirmLabel: 'Entfernen',
 *     tone: 'danger',                    // 'danger' | 'accent' (default 'accent')
 *   })
 *   if (ok) ...
 *
 * Wrap the app in <ConfirmProvider> once (alongside ToastProvider). Escape
 * cancels; the focused native button owns Enter to avoid accidental actions.
 */
const ConfirmContext = createContext(null)

export function ConfirmProvider ({ children }) {
  const [dialog, setDialog] = useState(null)
  const pendingResolveRef = useRef(null)
  const dialogRef = useDialogFocus(!!dialog)
  const confirm = useCallback((opts = {}) => {
    return new Promise(resolve => {
      // A double click or two concurrent callers must not orphan the first
      // promise. Replace it fail-closed before presenting the newest request.
      // The ref matters because rapid calls can arrive in one React batch,
      // before `dialog` has re-rendered.
      pendingResolveRef.current?.(false)
      pendingResolveRef.current = resolve
      setDialog({
        title: opts.title || 'Sind Sie sicher?',
        message: opts.message || '',
        confirmLabel: opts.confirmLabel || 'Bestätigen',
        cancelLabel: opts.cancelLabel || 'Abbrechen',
        tone: opts.tone || 'accent',
        resolve,
      })
    })
  }, [])
  const close = useCallback((result) => {
    setDialog(d => {
      if (d) {
        pendingResolveRef.current = null
        d.resolve(result)
      }
      return null
    })
  }, [])

  // A route change can unmount the provider while a caller awaits a decision.
  // Settle it as Cancel so no action hangs or continues unexpectedly.
  useEffect(() => () => {
    pendingResolveRef.current?.(false)
    pendingResolveRef.current = null
  }, [])

  // Escape cancels. Enter is intentionally left to the focused native button.
  useEffect(() => {
    if (!dialog) return
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(false) }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [dialog, close])

  const isDanger = dialog?.tone === 'danger'

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <div
          onClick={() => close(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(20,20,16,0.5)',
            backdropFilter: 'blur(4px)',
            display: 'grid', placeItems: 'center',
            padding: 16,
            animation: 'cc-confirm-fade 0.18s ease-out',
            fontFamily: 'Inter, -apple-system, sans-serif',
          }}
        >
          <style>{`
            @keyframes cc-confirm-fade {
              from { opacity: 0; transform: scale(0.985); }
              to { opacity: 1; transform: scale(1); }
            }
          `}</style>
          <div
            ref={dialogRef}
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cc-confirm-title"
            aria-describedby={dialog.message ? 'cc-confirm-message' : undefined}
            tabIndex={-1}
            style={{
              width: '100%', maxWidth: 420,
              background: '#ffffff',
              borderRadius: 16,
              boxShadow: '0 28px 64px rgba(20,20,16,0.36), 0 6px 14px rgba(20,20,16,0.08)',
              padding: 0,
              overflow: 'hidden',
            }}
          >
            {/* Header with icon + title + close */}
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              padding: '18px 18px 0',
            }}>
              <span style={{
                width: 36, height: 36, borderRadius: 10,
                background: isDanger ? '#fbeae6' : '#fff1e8',
                color: isDanger ? '#b3321c' : '#e85d1f',
                display: 'grid', placeItems: 'center',
                flexShrink: 0,
              }}>
                <AlertTriangle size={18} strokeWidth={2.2} />
              </span>
              <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
                <div
                  id="cc-confirm-title"
                  style={{
                    fontSize: 15, fontWeight: 700, color: '#1a1816',
                    letterSpacing: '-0.01em',
                    lineHeight: 1.35,
                  }}
                >
                  {dialog.title}
                </div>
              </div>
              <button
                type="button"
                onClick={() => close(false)}
                aria-label="Abbrechen"
                style={{
                  width: 28, height: 28, borderRadius: 8,
                  border: 0, background: '#fafaf7', color: '#6b6862',
                  cursor: 'pointer', display: 'grid', placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <X size={14} />
              </button>
            </div>

            {/* Message body */}
            {dialog.message && (
              <div id="cc-confirm-message" style={{
                padding: '12px 18px 0',
                marginLeft: 48,
                fontSize: 13.5, color: '#3a3733',
                lineHeight: 1.55,
                whiteSpace: 'pre-wrap',
              }}>
                {dialog.message}
              </div>
            )}

            {/* Actions */}
            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: 8,
              padding: '18px',
              marginTop: 16,
            }}>
              <button
                type="button"
                onClick={() => close(false)}
                autoFocus={isDanger}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #e7e5df',
                  background: '#ffffff',
                  color: '#3a3733',
                  borderRadius: 9,
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 13, fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background .12s',
                }}
                onMouseOver={e => { e.currentTarget.style.background = '#fafaf7' }}
                onMouseOut={e => { e.currentTarget.style.background = '#ffffff' }}
              >
                {dialog.cancelLabel}
              </button>
              <button
                type="button"
                onClick={() => close(true)}
                autoFocus={!isDanger}
                style={{
                  padding: '8px 16px',
                  border: 0,
                  background: isDanger ? '#b3321c' : '#e85d1f',
                  color: '#ffffff',
                  borderRadius: 9,
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 13, fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: isDanger
                    ? '0 6px 16px -8px rgba(179,50,28,0.5)'
                    : '0 6px 16px -8px rgba(232,93,31,0.5)',
                  transition: 'background .12s',
                }}
                onMouseOver={e => { e.currentTarget.style.background = isDanger ? '#922a17' : '#c84d12' }}
                onMouseOut={e => { e.currentTarget.style.background = isDanger ? '#b3321c' : '#e85d1f' }}
              >
                {dialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm () {
  const ctx = useContext(ConfirmContext)
  if (ctx) return ctx
  // Fallback so calls outside the provider don't crash. Returns a no-op
  // that resolves to false (treated as "Cancel").
  if (typeof console !== 'undefined') console.warn('[useConfirm] used outside ConfirmProvider')
  return async () => false
}

export default ConfirmProvider
